
import React, { useEffect, useState } from 'react';
import { TreatmentPlan, User as AppUser } from '../types';
import { getHistory } from '../services/treatmentService';
import { ArrowLeft, Clock, Search, Loader2, ChevronRight, User, Calendar, Zap, Sparkles, ShieldCheck } from 'lucide-react';

interface Props {
  user: AppUser;
  onBack: () => void;
  onSelectPlan: (plan: TreatmentPlan) => void;
}

const History: React.FC<Props> = ({ user, onBack, onSelectPlan }) => {
  const [plans, setPlans] = useState<TreatmentPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const data = await getHistory(user.id, user.profile);
      setPlans(data);
    } catch (e) {
      console.error("Erro ao carregar histórico:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [user.id, user.profile]);

  const filteredPlans = plans.filter(p => 
    p.patientName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8 animate-fade-in pb-20">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-3 rounded-xl bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-200 hover:bg-purple-200 transition-colors">
            <ArrowLeft size={24} />
          </button>
          <div>
            <h2 className="text-3xl font-bold text-chroma-text flex items-center gap-3">
              <Clock size={32} className="text-chroma-accent" />
              Histórico
            </h2>
            <div className="flex items-center gap-2">
               <p className="text-base text-chroma-muted font-light tracking-wide">Protocolos realizados</p>
               {user.profile === 'Master' && (
                 <span className="flex items-center gap-1 text-[10px] font-black uppercase text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                    <ShieldCheck size={10} /> Visão Global
                 </span>
               )}
            </div>
          </div>
        </div>

        <div className="flex gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-4 top-3.5 text-purple-400" size={18} />
            <input 
              type="text" 
              placeholder="Buscar paciente..."
              className="w-full bg-chroma-card border border-chroma-border rounded-xl py-3 pl-12 pr-4 text-sm focus:ring-2 focus:ring-chroma-accent/20 outline-none transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <Loader2 className="animate-spin text-chroma-accent" size={48} />
          <p className="text-lg font-bold tracking-wide text-chroma-muted uppercase text-[10px]">Acessando Banco de Dados...</p>
        </div>
      ) : plans.length === 0 ? (
        <div className="bg-chroma-card border border-chroma-border rounded-3xl p-20 text-center space-y-6">
           <div className="w-20 h-20 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto text-purple-400">
              <Zap size={40} />
           </div>
           <h3 className="text-2xl font-bold text-chroma-text">Nenhum protocolo encontrado</h3>
           <p className="text-lg font-light tracking-wide text-chroma-muted max-w-md mx-auto">
             Seus protocolos gerados aparecerão aqui. Se você acabou de criar um, tente atualizar a página.
           </p>
           <button onClick={fetchHistory} className="px-8 py-3 bg-purple-600 text-white rounded-xl font-bold">Atualizar Agora</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredPlans.map((plan) => (
            <button 
              key={plan.id}
              onClick={() => onSelectPlan(plan)}
              className="bg-chroma-card border border-chroma-border p-6 rounded-2xl flex items-center justify-between hover:border-chroma-accent/50 hover:shadow-xl transition-all group text-left"
            >
              <div className="flex items-center gap-6">
                 <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${plan.generationMethod === 'AI' ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>
                    {plan.generationMethod === 'AI' ? <Sparkles size={28} /> : <User size={28} />}
                 </div>
                 <div>
                    <h4 className="text-xl font-bold text-chroma-text group-hover:text-chroma-accent transition-colors">{plan.patientName}</h4>
                    <div className="flex flex-wrap items-center gap-4 mt-1 text-sm text-chroma-muted font-light tracking-wide">
                       <span className="flex items-center gap-1.5"><Calendar size={14} /> {plan.dateCreated}</span>
                       <span className="flex items-center gap-1.5 uppercase font-bold text-[10px] bg-purple-100 dark:bg-white/5 px-2 py-0.5 rounded-md">{plan.generationMethod}</span>
                    </div>
                 </div>
              </div>
              <ChevronRight className="text-purple-300 group-hover:text-chroma-accent transform group-hover:translate-x-1 transition-all" size={24} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default History;
